import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { brandService } from "@/services/api/cresivoxServer";
import { iBrand, REDUCER_NAME } from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

export const addBrand = createAsyncThunk<
  iGenericResponse<any | null> | null,
  {
    brandId?: string;
    name: string;
    description: string;
    image: string | File;
    category:string;
  },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("addBrand"),
  //@ts-ignore
  async (payload, { rejectWithValue }) => {
    try {
      const response = await brandService.addBrand(payload);

      if (response && response.error === null) {
        // Check if the response indicates success
        const { data } = response;
        return {
          data: {
            brands: data, // Assuming data contains the brand information
  //@ts-ignore
  message: data.message || "Brand added successfully",
          },
        };
      } else {
        // Handle the case where the response contains an error
        return rejectWithValue({
          message: response.error?.message || "An unknown error occurred",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);

export const getBrandList = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { page?: number; limit?: number; search?: string } | void,
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("brandList"),
  //@ts-ignore
  async (arg, { rejectWithValue }) => {
    try {
      const page = arg && typeof arg === 'object' ? arg.page : undefined;
      const limit = arg && typeof arg === 'object' ? arg.limit : undefined;
      const search = arg && typeof arg === 'object' ? arg.search : undefined;
      const response = await brandService.getBrandList(page, limit, search);
      if (response) {
        const { httpStatusCode, data, message } = response;

        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                httpStatusCode,
                data: data.data,
                message,
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);


export const getBrandById = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { brandId: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("getBrandById"),
  //@ts-ignore
  async ({ brandId }, { rejectWithValue }) => {
    try {
      const response = await brandService.getBrandById(brandId);

      if (response && response.error === null) {
        return {
          data: {
            data: response.data, // The actual brand data
          },
        };
      } else {
        return rejectWithValue({
          message: response?.error?.message || "Failed to fetch brand data",
        });
      }
    } catch (error) {
      console.error("Error fetching brand:", error);
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);

export const updateBrand = createAsyncThunk<
  iGenericResponse<any | null> | null,
  {
    brandId: string;
    name: string;
    description: string;
    image?: File | string;
    category:string;
  },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("updateBrand"),
  //@ts-ignore
  async (payload, { rejectWithValue }) => {
    try {
      const response = await brandService.updateBrand(payload);

      if (response && response.error === null) {
        return {
          data: {
            data: response.data,
  //@ts-ignore
  message: response.data.message || "Brand updated successfully",
          },
        };
      } else {
        return rejectWithValue({
          message: response?.error?.message || "Failed to update brand",
        });
      }
    } catch (error) {
      console.error("Error updating brand:", error); // Log the error
      return rejectWithValue({
        message: "Something went wrong while updating the brand",
      });
    }
  }
);

export const deleteBrand = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { brandId: string },
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("deleteBrand"),
  //@ts-ignore
  async ({ brandId }, { rejectWithValue }) => {
    try {
      const response = await brandService.deleteBrand(brandId);

      if (response && response.error === null) {
        return {
          data: {
            message: "Brand deleted successfully",
          },
        };
      } else {
        return rejectWithValue({
          message: response?.error?.message || "Failed to delete brand",
        });
      }
    } catch (error) {
      console.error("Error deleting brand:", error); // Log the error
      return rejectWithValue({
        message: "Something went wrong while deleting the brand",
      });
    }
  }
);
